import { isApiRequestError } from '@terros/common'
import type { ErrorEvent as SentryErrorEvent, EventHint } from '@sentry/react'

type RejectionEvent = Pick<PromiseRejectionEvent, 'preventDefault' | 'reason'>

/**
 * API mutation hooks already expose these failures to the UI for notification.
 * Treat them as handled at the browser boundary while leaving programming
 * errors visible to the browser and Sentry.
 */
export function handleUnhandledApiRejection(event: RejectionEvent): void {
  if (isApiRequestError(event.reason)) event.preventDefault()
}

/**
 * Sentry installs its own unhandled-rejection listener, so preventing the
 * browser's default behavior is not enough to stop capture. Drop only API
 * request errors captured through the unhandled-rejection mechanism. The
 * mechanism type is matched by suffix, not exact equality: SDK upgrades
 * rename it (v9 'onunhandledrejection', v10
 * 'auto.browser.global_handlers.onunhandledrejection'), and an exact match
 * silently disabled this filter once already. The type must still be
 * checked, because handled === false alone also matches uncaught
 * synchronous errors from the onerror handler, which are usually genuine
 * bugs and must stay visible.
 */
export function filterUnhandledApiRejection(event: SentryErrorEvent, hint: EventHint): SentryErrorEvent | null {
  const isUnhandledRejection = event.exception?.values?.some(
    (value) => value.mechanism?.handled === false && value.mechanism.type?.endsWith('onunhandledrejection')
  )

  if (!isUnhandledRejection) return event

  const originalException = getRejectionReason(hint.originalException)
  return isApiRequestError(originalException) ? null : event
}

function getRejectionReason(originalException: unknown): unknown {
  if (isPromiseRejectionEvent(originalException)) return originalException.reason
  return originalException
}

function isPromiseRejectionEvent(value: unknown): value is PromiseRejectionEvent {
  return typeof PromiseRejectionEvent !== 'undefined' && value instanceof PromiseRejectionEvent
}
